import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Settings, Send, Trash2, CheckCircle2, Bot } from 'lucide-react';
import { Card, Button, Input, LoadingSpinner, ErrorMessage } from '@/components/ui';
import { branchSettingsApi } from '@/services/api/branchSettingsApi';
import { queryKeys } from '@/services/api/queryClient';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const telegramSchema = z.object({
  bot_token: z.string().min(1, 'Введите токен бота'),
  chat_id: z.string().min(1, 'Введите ID чата'),
});

type TelegramForm = z.infer<typeof telegramSchema>;

const SettingsPage = () => {
  const queryClient = useQueryClient();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: queryKeys.branchSettings,
    queryFn: branchSettingsApi.getTelegram,
  });

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<TelegramForm>({
    resolver: zodResolver(telegramSchema),
  });

  const saveMutation = useMutation({
    mutationFn: branchSettingsApi.updateTelegram,
    onSuccess: () => {
      toast.success('Настройки сохранены');
      queryClient.invalidateQueries({ queryKey: queryKeys.branchSettings });
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при сохранении настроек')),
  });

  const testMutation = useMutation({
    mutationFn: branchSettingsApi.testTelegram,
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Тестовое сообщение отправлено');
      } else {
        toast.error('Не удалось отправить сообщение');
      }
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при отправке тестового сообщения')),
  });

  const revokeMutation = useMutation({
    mutationFn: branchSettingsApi.revokeTelegram,
    onSuccess: () => {
      toast.success('Telegram-уведомления отключены');
      queryClient.invalidateQueries({ queryKey: queryKeys.branchSettings });
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при отключении')),
  });

  const onSave = (data: TelegramForm) => {
    saveMutation.mutate(data);
  };

  const onTest = () => {
    const values = getValues();
    if (!values.bot_token || !values.chat_id) {
      toast.error('Заполните токен бота и ID чата');
      return;
    }
    testMutation.mutate(values);
  };

  if (isLoading) {
    return <LoadingSpinner size="lg" className="py-24" />;
  }

  if (error) {
    return <ErrorMessage message="Не удалось загрузить настройки" />;
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Настройки</h1>
        <p className="text-sm text-gray-500 mt-1">Настройки операционного центра</p>
      </div>

      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-10 h-10 rounded-xl flex items-center justify-center',
                settings?.is_configured ? 'bg-emerald-100' : 'bg-gray-100',
              )}>
                <Bot className={cn(
                  'w-5 h-5',
                  settings?.is_configured ? 'text-emerald-600' : 'text-gray-400',
                )} />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Telegram-уведомления</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                    settings?.is_configured
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-gray-100 text-gray-500',
                  )}>
                    <span className={cn(
                      'w-1.5 h-1.5 rounded-full',
                      settings?.is_configured ? 'bg-emerald-500' : 'bg-gray-400',
                    )} />
                    {settings?.is_configured ? 'Подключено' : 'Не настроено'}
                  </span>
                </div>
              </div>
            </div>
            <Settings className="w-5 h-5 text-gray-300" />
          </div>
        </Card.Header>
        <Card.Body>
          <div className="space-y-6">
            {settings?.is_configured && (
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Токен:</span>
                  <code className="px-2 py-0.5 rounded-md bg-white border border-gray-200 text-xs font-mono text-gray-700">
                    {settings.bot_token_masked}
                  </code>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">ID чата:</span>
                  <code className="px-2 py-0.5 rounded-md bg-white border border-gray-200 text-xs font-mono text-gray-700">
                    {settings.chat_id}
                  </code>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSave)} className="space-y-4">
              <Input
                label="Токен бота"
                type="password"
                placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                error={errors.bot_token?.message}
                required
                {...register('bot_token')}
              />

              <Input
                label="ID чата"
                placeholder="-1001234567890"
                error={errors.chat_id?.message}
                required
                {...register('chat_id')}
              />

              <div className="flex items-center gap-3 pt-2">
                <Button
                  type="submit"
                  loading={saveMutation.isPending}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Сохранить
                </Button>

                <Button
                  variant="outline"
                  onClick={onTest}
                  loading={testMutation.isPending}
                >
                  <Send className="w-4 h-4" />
                  Тест
                </Button>

                {settings?.is_configured && (
                  <Button
                    variant="ghost"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => revokeMutation.mutate()}
                    loading={revokeMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4" />
                    Отключить
                  </Button>
                )}
              </div>
            </form>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SettingsPage;
